const NUMBERED_PREFIX_RE = /^\s*(?:step\s+)?\d+[.):]\s*/i;
const SENTENCE_TERMINATOR_RE = /[.?!]\s+(?=[A-Z0-9])/;

const COMPOSITE_RE = /(\d+)\s*(hours?|hrs?|h)\s+(\d+)\s*(minutes?|mins?|m)/gi;
const HOUR_RE = /(\d+)\s*(hours?|hrs?|h)\b/gi;
const MIN_RE = /(\d+)\s*(minutes?|mins?|m)\b/gi;
const SEC_RE = /(\d+)\s*(seconds?|secs?|s)\b/gi;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function parseSteps(instructions: string): string[] {
  if (typeof instructions !== 'string' || instructions.trim().length === 0) {
    return [];
  }
  const lines = instructions
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.replace(NUMBERED_PREFIX_RE, '').trim())
    .filter((line) => line.length > 0);
  if (lines.length > 1 || instructions.includes('\n')) return lines;
  const fallback = instructions
    .split(SENTENCE_TERMINATOR_RE)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return fallback.length > 0 ? fallback : lines;
}

interface DurationMatch {
  index: number;
  totalSeconds: number;
  label: string;
}

function earliest(re: RegExp, text: string, build: (m: RegExpExecArray) => DurationMatch | null): DurationMatch | null {
  re.lastIndex = 0;
  const m = re.exec(text);
  if (!m) return null;
  return build(m);
}

const COMPOSITE_BUILDER = (m: RegExpExecArray): DurationMatch | null => {
  const h = Number.parseInt(m[1] ?? '', 10);
  const min = Number.parseInt(m[3] ?? '', 10);
  if (!Number.isFinite(h) || !Number.isFinite(min)) return null;
  if (h <= 0 && min <= 0) return null;
  const parts: string[] = [];
  if (h > 0) parts.push(`${h} H`);
  if (min > 0) parts.push(`${min} Min`);
  return {
    index: m.index,
    totalSeconds: h * 3600 + min * 60,
    label: parts.join(' '),
  };
};

const HOUR_BUILDER = (m: RegExpExecArray): DurationMatch | null => {
  const v = Number.parseInt(m[1] ?? '', 10);
  if (!Number.isFinite(v) || v <= 0) return null;
  return { index: m.index, totalSeconds: v * 3600, label: `${v} H` };
};

const MIN_BUILDER = (m: RegExpExecArray): DurationMatch | null => {
  const v = Number.parseInt(m[1] ?? '', 10);
  if (!Number.isFinite(v) || v <= 0) return null;
  return { index: m.index, totalSeconds: v * 60, label: `${v} Min` };
};

const SEC_BUILDER = (m: RegExpExecArray): DurationMatch | null => {
  const v = Number.parseInt(m[1] ?? '', 10);
  if (!Number.isFinite(v) || v <= 0) return null;
  return { index: m.index, totalSeconds: v, label: `${v} Sec` };
};

export function extractFirstDuration(
  text: string,
): { totalSeconds: number; label: string } | null {
  if (typeof text !== 'string' || text.length === 0) return null;
  const candidates: DurationMatch[] = [];
  const c1 = earliest(COMPOSITE_RE, text, COMPOSITE_BUILDER);
  if (c1) candidates.push(c1);
  const c2 = earliest(HOUR_RE, text, HOUR_BUILDER);
  if (c2 && c2.index >= (c1?.index ?? 0)) {
    candidates.push(c2);
  }
  const c3 = earliest(MIN_RE, text, MIN_BUILDER);
  if (c3) candidates.push(c3);
  const c4 = earliest(SEC_RE, text, SEC_BUILDER);
  if (c4) candidates.push(c4);
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => a.index - b.index);
  const first = candidates[0]!;
  return { totalSeconds: first.totalSeconds, label: first.label };
}

export function formatMmSs(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '00:00';
  const s = Math.floor(totalSeconds);
  const mm = Math.floor(s / 60);
  const ss = s % 60;
  return `${pad2(mm)}:${pad2(ss)}`;
}

export function scaleIngredientAmount(
  baseAmount: number,
  portions: number,
  defaultPortions: number,
): number {
  if (!Number.isFinite(baseAmount) || !Number.isFinite(portions) || !Number.isFinite(defaultPortions)) {
    return 0;
  }
  if (defaultPortions <= 0) return baseAmount;
  return baseAmount * (portions / defaultPortions);
}

export function formatAmount(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 10) / 10;
  if (Math.abs(rounded - Math.trunc(rounded)) < 0.05) {
    return String(Math.trunc(rounded));
  }
  return rounded.toFixed(1).replace(/\.0$/, '');
}
